import { useAuth0 } from '@auth0/auth0-react';
import { CSSProperties, Key, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE, nonStandardPeptidesColor, peptideColorPaletteVdW } from '../../../../compute/colormap';
import { selectExcludedPeptides, selectExcludedSpots, setExcludedPeptides, setExcludedSpots } from '../../../../features/analysisConfig/analysisConfigSlice';
import { PeptideSet } from '../../../../types/analysisTypes';
import { FeatureFlag, UserClaimsWithTSDB } from '../../../../types/userType';
import { Button, Col, Dropdown, MenuProps } from 'antd';
import { SensorSubset } from '../PeptidesSelectorSettings';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

type PeptidesSelectorGridProps = {
  style: { [K in Key]: CSSProperties };
  spots: string[] | undefined;
  peptidesSetType: PeptideSet;
  weakContrastSpots: string[];
  sensorAutoSelectResult: SensorSubset | undefined;
};

const presetLabels: Record<string, string> = {
  allok: 'All OK',
  auto: 'Auto',
  empty: 'Empty',
  POR4: 'POR4',
  'P&LaC': 'P&LaC',
  P: 'P',
  TR: 'TR',
  TRaC: 'TRaC',
};

const PeptidesSelectorGrid: React.FC<PeptidesSelectorGridProps> = ({ style, spots, peptidesSetType, weakContrastSpots, sensorAutoSelectResult }) => {
  const excludedPeptides = useAppSelector(selectExcludedPeptides);
  const excludedSpots = useAppSelector(selectExcludedSpots);
  const [peptides, setPeptides] = useState<string[] | undefined>();
  const [selectedFilterPreset, setSelectedFilterPreset] = useState<string>('allok');

  const dispatch = useAppDispatch();

  const [userInfo, setUserInfo] = useState<UserClaimsWithTSDB | null>(null);
  const { user, isAuthenticated } = useAuth0();

  const autoSolutionItems: MenuProps['items'] = [
    { key: 'auto:optimal', label: 'Optimal' },
    ...(sensorAutoSelectResult?.Metrics.Count.map((v, idx) => ({
      key: `auto:${v}`,
      label: `${v} sensors (r=${sensorAutoSelectResult?.Metrics.Ratio[idx].toFixed(1)})`,
    })).reverse() || []),
  ];

  const presetItems: MenuProps['items'] = Object.entries(presetLabels).map(([key, label]) => ({
    key,
    label:
      key === 'auto' ? (
        <Dropdown
          trigger={['hover']}
          align={{ points: ['tl', 'tr'], offset: [0, 0], overflow: { adjustX: false, adjustY: false } }}
          disabled={!sensorAutoSelectResult}
          menu={{
            items: autoSolutionItems,
            style: {
              maxHeight: 240,
              overflowY: 'auto',
              overflowX: 'hidden',
            },
            onClick: ({ key }) => {
              onSelectedPreset(key);
            },
          }}
        >
          <span
            style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', opacity: !sensorAutoSelectResult ? 0.45 : 1, cursor: !sensorAutoSelectResult ? 'not-allowed' : 'pointer' }}
            onClick={(event) => event.stopPropagation()}
          >
            Auto <RightOutlined />{' '}
          </span>
        </Dropdown>
      ) : (
        label
      ),
  }));

  useEffect(() => {
    if (!user || !isAuthenticated) setUserInfo(null);
    else setUserInfo(user as UserClaimsWithTSDB);
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (spots) {
      if (userInfo && userInfo.feature_flags.includes(FeatureFlag.AANonAggregatedPeptidesAccessEnabled)) setPeptides(Array.from(new Set(spots.map((s) => parseInt(s.toString()).toString()))));
      else setPeptides(spots);
    }
  }, [spots, userInfo]);

  const createStyle = (peptide: string) => {
    const featureFlag = userInfo && userInfo.feature_flags.includes(FeatureFlag.AANonAggregatedPeptidesAccessEnabled);
    let containsSomeSpots: boolean = false;

    if (featureFlag === true) {
      const matchingSpots = spots?.filter((s) => parseInt(s.toString()).toString() === peptide);
      let containsAllSpots: boolean = false;
      let peptideCoordinateInts: string[] = [];

      if (matchingSpots) {
        peptideCoordinateInts = matchingSpots?.map((s) => s.split('[')[1].split(']')[0]);
        const containsSpots: boolean[] = peptideCoordinateInts.map((spot) => excludedSpots.includes(spot));
        containsAllSpots = !containsSpots.includes(false);
        containsSomeSpots = containsSpots.includes(true);
      }

      if (containsAllSpots) return style.diselectedPeptides;
    } else {
      if (excludedPeptides.findIndex((el) => el === peptide) !== -1) return style.diselectedPeptides;
    }

    if (peptidesSetType !== PeptideSet.NonStandard) {
      let color = DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE;

      if (peptidesSetType === PeptideSet.POR1 || peptidesSetType === PeptideSet.POR2 || peptidesSetType === PeptideSet.POR3 || peptidesSetType === PeptideSet.POR4) {
        color = peptideColorPaletteVdW[parseInt(peptide)];
        if (!color) color = DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE;
      }

      if (featureFlag === true && containsSomeSpots) return { color: color, border: `2px solid ${color}`, background: 'white', fontWeight: 700 };
      else return { backgroundColor: color };
    }

    if (peptidesSetType === PeptideSet.NonStandard) {
      let color = nonStandardPeptidesColor[Number(peptide) % nonStandardPeptidesColor.length];
      if (featureFlag === true && containsSomeSpots) return { color: color, border: `2px solid ${color}`, background: 'white', fontWeight: 700 };
      else return { backgroundColor: color };
    }
    if (featureFlag === true && containsSomeSpots) return { color: DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE, border: `2px solid ${DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE}`, background: 'white', fontWeight: 700 };
    else return { backgroundColor: DEFAULT_COLOR_FOR_UNKNOWN_PEPTIDE };
  };

  const excludePeptides = (peptide: string) => {
    if (userInfo === null || userInfo.feature_flags.includes(FeatureFlag.AALightViewQualityControl)) return;

    if (userInfo && userInfo.feature_flags.includes(FeatureFlag.AANonAggregatedPeptidesAccessEnabled)) {
      const matchingSpots = spots?.filter((s) => parseInt(s.toString()).toString() === peptide);

      let containsAllSpots: boolean = false;
      let peptideCoordinateInts: string[] = [];

      if (matchingSpots) {
        peptideCoordinateInts = matchingSpots?.map((s) => s.split('[')[1].split(']')[0]);
        const containsSpots: boolean[] = peptideCoordinateInts.map((spot) => excludedSpots.includes(spot));
        containsAllSpots = !containsSpots.includes(false);
      }

      if (containsAllSpots === false) {
        dispatch(setExcludedSpots(Array.from(new Set([...excludedSpots, ...peptideCoordinateInts]))));
      } else {
        dispatch(setExcludedSpots(excludedSpots.filter((spot) => !peptideCoordinateInts.includes(spot))));
      }
    } else {
      if (excludedPeptides.findIndex((el) => el === peptide) === -1) dispatch(setExcludedPeptides([...excludedPeptides, peptide]));
      else {
        const newEcludedSpots = excludedPeptides.filter((el) => el !== peptide);
        dispatch(setExcludedPeptides(newEcludedSpots));
      }
    }
  };

  const onSelectedPreset = (val: string) => {
    let peptidesToExclude: string[] = [];
    let spotsToExclude: string[] = [];

    if (val.startsWith('auto')) {
      let autoType = val.split(':')[1];
      if (autoType === 'optimal') {
        peptidesToExclude = peptides?.filter((p) => !sensorAutoSelectResult?.Sensors.includes(p)) || [];
      } else {
        const sensors = sensorAutoSelectResult?.Metrics.Sensors.find((_, idx) => `${sensorAutoSelectResult.Metrics.Count[idx]}` === autoType) || [];
        peptidesToExclude = peptides?.filter((p) => !sensors.includes(p)) || [];
      }
    } else {
      switch (val) {
        case 'POR4':
          peptidesToExclude = peptides?.filter((p) => !['1', '24', '25', '34', '36', '55', '105', '106'].includes(p)) || [];
          break;
        case 'P&LaC':
          peptidesToExclude = peptides?.filter((p) => !['1', '24', '25', '34', '36', '55', '1112', '1192', '5108', '6528'].includes(p)) || [];
          break;
        case 'P':
          peptidesToExclude = peptides?.filter((p) => !['1', '34', '55', '1112', '1192', '5108', '6528'].includes(p)) || [];
          break;
        case 'TR':
          peptidesToExclude = peptides?.filter((p) => !['1', '24', '55', '1112', '1192', '2803', '4286', '5108', '6305', '6528', '6533'].includes(p)) || [];
          break;
        case 'TRaC':
          peptidesToExclude = peptides?.filter((p) => !['1', '24', '25', '34', '36', '55', '1112', '1192', '2546', '2803', '4286', '4487', '5108', '6305', '6528', '6533', '8802', '9512'].includes(p)) || [];
          break;
        case 'empty':
          peptidesToExclude = peptides || [];
          break;
        case 'allok':
          peptidesToExclude = [];
          spotsToExclude = weakContrastSpots;
          break;
      }
    }

    if (val !== 'allok') {
      spotsToExclude = spots?.filter((s) => peptidesToExclude.includes(parseInt(s.toString()).toString())) || [];
      spotsToExclude = spotsToExclude.map((s) => s.split('[')[1].split(']')[0]);
    }

    if (userInfo && userInfo.feature_flags.includes(FeatureFlag.AANonAggregatedPeptidesAccessEnabled)) {
      dispatch(setExcludedSpots(spotsToExclude));
    } else {
      dispatch(setExcludedPeptides(peptidesToExclude));
    }

    setSelectedFilterPreset(val);
  };

  return (
    <Col>
      <Dropdown
        trigger={['click']}
        menu={{
          items: presetItems,
          onClick: ({ key }) => {
            if (key !== 'auto') onSelectedPreset(key);
          },
        }}
      >
        <Button>
          {selectedFilterPreset.startsWith('auto:') ? `Auto (${selectedFilterPreset.split(':')[1]})` : presetLabels[selectedFilterPreset] ?? selectedFilterPreset}
          <DownOutlined />
        </Button>
      </Dropdown>
      <table style={{ borderCollapse: 'separate', marginTop: 25 }}>
        <tbody>
          <tr style={{ display: 'flex', flexWrap: 'wrap' }}>
            {peptides?.map((el, index) => (
              <td style={{ ...style.base, ...style.td, ...{ margin: 2 }, ...createStyle(el) }} key={index} onClick={() => excludePeptides(el)}>
                {el}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </Col>
  );
};

export default PeptidesSelectorGrid;
